// src/index.ts
import { getAxios } from "@coremind/identity-core";
var api = () => getAxios();
var normalizeMenuNode = (value) => {
  if (!value || typeof value !== "object") return null;
  const key = typeof value.key === "string" && value.key.trim() ? value.key.trim() : "";
  const label = typeof value.label === "string" && value.label.trim() ? value.label.trim() : "";
  if (!key || !label) return null;
  return {
    key,
    label,
    route_path: typeof value.route_path === "string" ? value.route_path : value.route_path ?? null,
    icon_name: typeof value.icon_name === "string" ? value.icon_name : value.icon_name ?? null,
    component_key: typeof value.component_key === "string" ? value.component_key : value.component_key ?? null,
    parent_key: typeof value.parent_key === "string" ? value.parent_key : value.parent_key ?? null,
    shell_header: typeof value.shell_header === "string" ? value.shell_header : value.shell_header ?? null,
    section: typeof value.section === "string" ? value.section : value.section ?? null,
    kind: typeof value.kind === "string" ? value.kind : value.kind ?? null,
    sort_order: typeof value.sort_order === "number" ? value.sort_order : null,
    groups: Array.isArray(value.groups) ? value.groups.filter((entry) => typeof entry === "string") : [],
    llm_schema_output: value.llm_schema_output && typeof value.llm_schema_output === "object" ? value.llm_schema_output : null
  };
};
var normalizeMenuConfig = (value) => {
  if (!value || typeof value !== "object") return null;
  return {
    shell_header: typeof value.shell_header === "string" ? value.shell_header : value.shell_header ?? null,
    default_section: typeof value.default_section === "string" ? value.default_section : value.default_section ?? null,
    nodes: Array.isArray(value.nodes) ? value.nodes.map((node) => normalizeMenuNode(node)).filter(Boolean) : []
  };
};
var serializeMenuNode = (node) => ({
  key: node.key,
  label: node.label,
  ...node.route_path !== void 0 ? { route_path: node.route_path } : {},
  ...node.icon_name !== void 0 ? { icon_name: node.icon_name } : {},
  ...node.component_key !== void 0 ? { component_key: node.component_key } : {},
  ...node.parent_key !== void 0 ? { parent_key: node.parent_key } : {},
  ...node.shell_header !== void 0 ? { shell_header: node.shell_header } : {},
  ...node.section !== void 0 ? { section: node.section } : {},
  ...node.kind !== void 0 ? { kind: node.kind } : {},
  ...node.sort_order !== void 0 ? { sort_order: node.sort_order } : {},
  ...node.groups !== void 0 ? { groups: node.groups } : {},
  ...node.llm_schema_output !== void 0 ? { llm_schema_output: node.llm_schema_output } : {}
});
var serializeMenuConfig = (menu) => ({
  ...menu.shell_header !== void 0 ? { shell_header: menu.shell_header } : {},
  ...menu.default_section !== void 0 ? { default_section: menu.default_section } : {},
  ...menu.nodes !== void 0 ? { nodes: menu.nodes.map(serializeMenuNode) } : {}
});
var normalizePayload = (config) => {
  const payload = config.payload || {};
  const componentKey = payload.component_key || config.name;
  const accessMode = String(payload.access_mode || "request").toLowerCase();
  const defaultEnabled = Boolean(payload.default_enabled) || accessMode === "default";
  return {
    component_key: String(componentKey),
    label: String(payload.label || config.name),
    route_path: payload.route_path ?? null,
    icon_name: payload.icon_name ?? null,
    summary: payload.summary ?? null,
    image_url: payload.image_url ?? null,
    labels: Array.isArray(payload.labels) ? payload.labels : [],
    category: payload.category ?? null,
    featured: Boolean(payload.featured),
    featured_rank: typeof payload.featured_rank === "number" ? payload.featured_rank : null,
    groups: Array.isArray(payload.groups) ? payload.groups : [],
    developer_user_ids: Array.isArray(payload.developer_user_ids) ? payload.developer_user_ids : [],
    visibility: payload.visibility ?? "listed",
    access_mode: accessMode,
    default_enabled: defaultEnabled,
    requestable: typeof payload.requestable === "boolean" ? payload.requestable : accessMode === "request" || accessMode === "paid",
    price_monthly: typeof payload.price_monthly === "number" ? payload.price_monthly : null,
    price_one_time: typeof payload.price_one_time === "number" ? payload.price_one_time : null,
    admin_only: Boolean(payload.admin_only),
    app_type: payload.app_type === "system" || payload.app_type === "app" ? payload.app_type : payload.app_type === "service" || payload.app_type === "admin" ? "system" : "app",
    publish_level: payload.publish_level === "alpha" || payload.publish_level === "beta" || payload.publish_level === "qa" || payload.publish_level === "testing" || payload.publish_level === "nonprod" || payload.publish_level === "prod" ? payload.publish_level : "prod",
    dev_mode: Boolean(payload.dev_mode),
    shell_header: payload.shell_header ?? null,
    menu_section: payload.menu_section ?? null,
    menu: normalizeMenuConfig(payload.menu)
  };
};
var toAppConfig = (config) => ({
  ...config,
  payload: normalizePayload(config)
});
var listAppConfigs = async () => {
  const response = await api().get("/admin/configs");
  return response.data.filter((config) => config.config_type.toLowerCase() === "app").map(toAppConfig);
};
var createAppConfig = async (payload) => {
  const response = await api().post("/admin/configs", {
    config_type: "app",
    name: payload.component_key,
    description: payload.description ?? void 0,
    payload: {
      component_key: payload.component_key,
      label: payload.label,
      route_path: payload.route_path ?? null,
      icon_name: payload.icon_name ?? null,
      summary: payload.summary ?? null,
      image_url: payload.image_url ?? null,
      labels: payload.labels ?? [],
      category: payload.category ?? null,
      featured: payload.featured ?? false,
      featured_rank: payload.featured_rank ?? null,
      groups: payload.groups ?? [],
      developer_user_ids: payload.developer_user_ids ?? [],
      ...payload.visibility !== void 0 ? { visibility: payload.visibility } : {},
      ...payload.access_mode !== void 0 ? { access_mode: payload.access_mode } : {},
      ...payload.default_enabled !== void 0 ? { default_enabled: payload.default_enabled } : {},
      ...payload.requestable !== void 0 ? { requestable: payload.requestable } : {},
      ...payload.price_monthly !== void 0 ? { price_monthly: payload.price_monthly } : {},
      ...payload.price_one_time !== void 0 ? { price_one_time: payload.price_one_time } : {},
      ...payload.admin_only !== void 0 ? { admin_only: payload.admin_only } : {},
      ...payload.app_type !== void 0 ? { app_type: payload.app_type } : {},
      ...payload.publish_level !== void 0 ? { publish_level: payload.publish_level } : {},
      ...payload.dev_mode !== void 0 ? { dev_mode: payload.dev_mode } : {},
      ...payload.shell_header !== void 0 ? { shell_header: payload.shell_header } : {},
      ...payload.menu_section !== void 0 ? { menu_section: payload.menu_section } : {},
      ...payload.menu !== void 0 ? { menu: serializeMenuConfig(payload.menu) } : {}
    },
    is_active: payload.is_active ?? true
  });
  return toAppConfig(response.data);
};
var updateAppConfig = async (configId, payload) => {
  const response = await api().put(`/admin/configs/${configId}`, {
    name: payload.component_key ?? void 0,
    description: payload.description ?? void 0,
    payload: {
      ...payload.component_key !== void 0 ? { component_key: payload.component_key } : {},
      ...payload.label !== void 0 ? { label: payload.label } : {},
      ...payload.route_path !== void 0 ? { route_path: payload.route_path } : {},
      ...payload.icon_name !== void 0 ? { icon_name: payload.icon_name } : {},
      ...payload.summary !== void 0 ? { summary: payload.summary } : {},
      ...payload.image_url !== void 0 ? { image_url: payload.image_url } : {},
      ...payload.labels !== void 0 ? { labels: payload.labels } : {},
      ...payload.category !== void 0 ? { category: payload.category } : {},
      ...payload.featured !== void 0 ? { featured: payload.featured } : {},
      ...payload.featured_rank !== void 0 ? { featured_rank: payload.featured_rank } : {},
      ...payload.groups !== void 0 ? { groups: payload.groups } : {},
      ...payload.developer_user_ids !== void 0 ? { developer_user_ids: payload.developer_user_ids } : {},
      ...payload.visibility !== void 0 ? { visibility: payload.visibility } : {},
      ...payload.access_mode !== void 0 ? { access_mode: payload.access_mode } : {},
      ...payload.default_enabled !== void 0 ? { default_enabled: payload.default_enabled } : {},
      ...payload.requestable !== void 0 ? { requestable: payload.requestable } : {},
      ...payload.price_monthly !== void 0 ? { price_monthly: payload.price_monthly } : {},
      ...payload.price_one_time !== void 0 ? { price_one_time: payload.price_one_time } : {},
      ...payload.admin_only !== void 0 ? { admin_only: payload.admin_only } : {},
      ...payload.app_type !== void 0 ? { app_type: payload.app_type } : {},
      ...payload.publish_level !== void 0 ? { publish_level: payload.publish_level } : {},
      ...payload.dev_mode !== void 0 ? { dev_mode: payload.dev_mode } : {},
      ...payload.shell_header !== void 0 ? { shell_header: payload.shell_header } : {},
      ...payload.menu_section !== void 0 ? { menu_section: payload.menu_section } : {},
      ...payload.menu !== void 0 ? { menu: serializeMenuConfig(payload.menu) } : {}
    },
    is_active: payload.is_active ?? void 0,
    version: payload.version ?? void 0
  });
  return toAppConfig(response.data);
};
var listAppAccessRequests = async (statuses) => {
  const response = await api().get("/apps/access-requests", {
    params: statuses?.length ? { status: statuses } : void 0
  });
  return response.data;
};
var bulkUpdateAppAccessRequests = async (payload) => {
  const response = await api().post(
    "/apps/access-requests/bulk",
    payload
  );
  return response.data;
};
var listAppFeedback = async (componentKeys) => {
  const response = await api().get("/apps/feedback", {
    params: componentKeys?.length ? { component_keys: componentKeys } : void 0
  });
  return response.data;
};
var listAppScopes = async (params) => {
  const response = await api().get("/apps/scopes", { params });
  return response.data;
};
var createAppScope = async (payload) => {
  const response = await api().post("/apps/scopes", payload);
  return response.data;
};
var updateAppScope = async (scopeId, payload) => {
  const response = await api().patch(`/apps/scopes/${scopeId}`, payload);
  return response.data;
};
var listSiteDeploymentGraphs = async () => {
  const response = await api().get("/platform/deployments/sites");
  return response.data;
};
export {
  bulkUpdateAppAccessRequests,
  createAppConfig,
  createAppScope,
  listAppAccessRequests,
  listAppConfigs,
  listAppFeedback,
  listAppScopes,
  listSiteDeploymentGraphs,
  updateAppConfig,
  updateAppScope
};
